import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Divider, Segmented, message, Button, Input, DatePicker } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  format,
  startOfToday,
  endOfToday,
  startOfDay,
  endOfDay,
} from "date-fns";

import { cn } from "../../../lib/utils";

import { getTaskSummary } from "../../../api/dashboard";
import dayjs from "dayjs";
import WeeklyCalendar from "./WeeklyCalendar";
import MonthlyCalendar from "./MonthlyCalendar";
import DailyCalendar from "./DailyCalendar";
import { useDebounce } from 'use-debounce';

const { Search } = Input;

export default function CalendarSection({ equipments, users }) {
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState({
    projects: [],
    equipments,
    tasks: [],
  });

  const [view, setView] = useState("day");
  const [dateFormat, setDateFormat] = useState("DD MMMM, YYYY");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [query, setQuery] = useState(null);
  const [debouncedQuery] = useDebounce(query, 300)

  const [userQuery, setUserQuery] = useState(null);
  const [debouncedUserQuery] = useDebounce(userQuery, 300)

  const fetchSummary = async () => {
    if (!startDate || !endDate) return;
    setIsLoading(true);
    try {
      const resSummary = await getTaskSummary({
        from: startDate.toISOString(),
        to: endDate.toISOString(),
      });
      setSummary({ ...summary, ...resSummary });
      setIsLoading(false);
    } catch (error) {
      console.log(error);
      message.error(error);
      setIsLoading(false);
    }
  };

  const onDateChange = (e) => {
    if (view === "month") {
      setStartDate(startOfMonth(e.toDate()));
      setEndDate(endOfMonth(e.toDate()));
    } else if (view === "week") {
      setStartDate(startOfWeek(e.toDate()));
      setEndDate(endOfWeek(e.toDate()));
    } else if (view === "day") {
      setStartDate(startOfDay(e.toDate()));
      setEndDate(endOfDay(e.toDate()));
    }
  };


  const filteredSummary = useMemo(() => {
    // If no query, return the original summary
    if (!debouncedQuery && !debouncedUserQuery) return summary;

    let matchedProjects = summary.projects;
    let matchedTasks = summary.tasks;
    let matchedEquipment = summary.equipments;

    if (debouncedQuery) {
      const normalizedQuery = debouncedQuery.toLowerCase();

      // Find all matching projects, tasks, and equipment
      matchedProjects = summary.projects.filter((p) =>
        p.name.toLowerCase().includes(normalizedQuery) &&
        p.projectEquipments && p.projectEquipments.length > 0
      );
      matchedTasks = summary.tasks.filter((t) =>
        t.name.toLowerCase().includes(normalizedQuery) &&
        t.equipment
      );
      matchedEquipment = summary.equipments.filter((e) =>
        e.name.toLowerCase().includes(normalizedQuery) ||
        e.nickName.toLowerCase().includes(normalizedQuery)
      );

      // Filter equipment based on matched projects or tasks
      let filteredEquipments = [];

      if (matchedEquipment.length > 0) {
        filteredEquipments = [...matchedEquipment];
      }

      if (matchedProjects.length > 0) {
        const projectEquipments = matchedProjects.flatMap(project =>
          project.projectEquipments.map(e => e.equipment)
        );
        filteredEquipments = [...filteredEquipments, ...projectEquipments];
      }

      if (matchedTasks.length > 0) {
        const taskEquipments = matchedTasks.map(task => task.equipment);
        filteredEquipments = [...filteredEquipments, ...taskEquipments];
      }

      // Include tasks associated with matched equipment
      if (matchedEquipment.length > 0) {
        const equipmentTasks = summary.tasks.filter((t) =>
          matchedEquipment.some((e) => t.equipment && t.equipment.id === e.id)
        );

        matchedTasks.push(...equipmentTasks);
      }

      // Remove duplicates (if any)
      filteredEquipments = [...new Set(filteredEquipments)];

      matchedEquipment = filteredEquipments.length > 0 ? filteredEquipments : summary.equipments;
    }

    if (debouncedUserQuery) {
      const normalizedUserQuery = debouncedUserQuery.toLowerCase();
      console.log(matchedTasks)
      // Filter by user query if it exists
      matchedTasks = matchedTasks.filter((t) => {
        const firstName = t.assignee.firstName;
        const lastName = t.assignee.lastName;
        const fullName = `${t.assignee.firstName} ${t.assignee.lastName}`
        return (
          t.assignee && (
            (firstName && firstName.toLowerCase().includes(normalizedUserQuery)) ||
            (lastName && lastName.toLowerCase().includes(normalizedUserQuery)) ||
            (fullName && fullName.toLowerCase().includes(normalizedUserQuery)) ||
            (t.assignee.email && t.assignee.email.toLowerCase().includes(normalizedUserQuery))
          )
        )
      }
      );
      // Only show equipment that contains the matched tasks
      matchedEquipment = matchedEquipment.filter((e) =>
        matchedTasks.some((t) => t.equipment && t.equipment.id === e.id)
      );
    }
    // Return filtered summary with all matching results
    return {
      projects: matchedProjects.length > 0 ? matchedProjects : summary.projects,
      tasks: matchedTasks.length > 0 ? matchedTasks : summary.tasks,
      equipments: matchedEquipment.length > 0 ? matchedEquipment : summary.equipments,
    };
  }, [debouncedQuery, debouncedUserQuery, summary]);

  useEffect(() => {
    if (!startDate || !endDate) return;
    fetchSummary();
  }, [startDate, endDate]);

  useEffect(() => {
    if (view === "month") {
      setStartDate(startOfMonth(new Date()));
      setEndDate(endOfMonth(new Date()));
    } else if (view === "week") {
      setStartDate(startOfWeek(new Date()));
      setEndDate(endOfWeek(new Date()));
    } else if (view === "day") {
      setStartDate(startOfToday());
      setEndDate(endOfToday());
    }

  }, [view]);


  useEffect(() => {
    view === "day"
      ? setDateFormat("DD MMMM, YYYY")
      : view === "week"
        ? setDateFormat("wo - YYYY")
        : setDateFormat("MMMM, YYYY");
  }, [view]);


  return (
    <div className="p-4 bg-white">
      <div>
        <div className="flex justify-between items-baseline">
          <Segmented
            disabled={isLoading}
            options={[
              { label: "Daily", value: "day" },
              { label: "Weekly", value: "week" },
              { label: "Monthly", value: "month" },
            ]}
            value={view}
            onChange={setView}
          />
          <div className="flex flex-col items-center">
            <div className="flex items-baseline gap-2">
              <Button
                icon={<LeftOutlined />}
                onClick={() => {
                  if (view === "day") {
                    const newDayDate = dayjs(startDate)
                      .clone()
                      .subtract(1, "day")
                      .toDate();
                    setStartDate(newDayDate);
                  } else if (view === "week") {
                    const newWeekDate = dayjs(startDate)
                      .clone()
                      .subtract(1, "week")
                      .toDate();
                    setStartDate(newWeekDate);
                  } else {
                    const newMonthDate = dayjs(startDate)
                      .clone()
                      .subtract(1, "month")
                      .toDate();
                    setStartDate(newMonthDate);
                  }
                }}
              />
              <DatePicker
                value={dayjs(startDate)}
                onChange={onDateChange}
                format={dateFormat}
                picker={view}
                size="large"
              />
              <Button
                icon={<RightOutlined />}
                onClick={() => {
                  if (view === "day") {
                    const newDayDate = dayjs(startDate)
                      .clone()
                      .add(1, "day")
                      .toDate();
                    setStartDate(newDayDate);
                    setEndDate(endOfDay(newDayDate))
                  } else if (view === "week") {
                    const newWeekDate = dayjs(startDate)
                      .clone()
                      .add(1, "week")
                      .toDate();
                    setStartDate(newWeekDate);
                    setEndDate(endOfWeek(newWeekDate))
                  } else {
                    const newMonthDate = dayjs(startDate)
                      .clone()
                      .add(1, "month")
                      .toDate();
                    setStartDate(newMonthDate);
                    setEndDate(endOfMonth(newMonthDate))
                  }
                }}
              />
            </div>
            {/* {(view === "month" || view === "week") && startDate && (
              <h5 className="text-lg font-light ">
                {format(startDate, "MMMM, yyy")}
              </h5>
            )} */}
            {view === "day" && startDate && (
              <div className="flex flex-col items-center">
                <h5 className="text-lg font-light ">
                  {format(startDate, "EEEE")}
                </h5>
              </div>
            )}
          </div>
          <div className="search-boxes flex gap-2">

            <Search
              disabled={isLoading}
              placeholder="Search by assignee"
              className={cn("w-64", view === "month" ? "hidden" : "")}
              allowClear
              onChange={(e) => setUserQuery(e.target.value)}
            />
            <Search
              disabled={isLoading}
              placeholder="Equipment, task or project"
              className={cn("w-64", view === "month" ? "hidden" : "")}
              allowClear
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          {view === "month" && <div className="w-64" />}
        </div>
        <Divider />
      </div>

      <div>
        {view === "day" && filteredSummary && (
          <DailyCalendar
            tasks={filteredSummary.tasks}
            equipments={filteredSummary.equipments}
            date={startDate}
            onTaskUpdate={() => {
              fetchSummary();
            }}
          />
        )}
        {view === "week" && filteredSummary && (
          <WeeklyCalendar
            tasks={filteredSummary.tasks}
            equipments={filteredSummary.equipments}
            startDate={startDate}
            onTaskUpdate={() => {
              fetchSummary();
            }}
          />
        )}
        {view === "month" && (
          <MonthlyCalendar
            calendarValue={dayjs(startDate)}
            equipments={equipments}
            users={users}
            projects={filteredSummary.projects}
          />
        )}
      </div>
    </div>
  );
}
